import { cloneDeep, get, groupBy, uniqBy } from 'lodash'
import {
  GET_SCHOOL_STAFFS,
  WORKING_HOURS,
  GET_SCHOOL_TREE,
  GET_SCHOOL_INFO,
  GET_SCHOOL_STAFF_DESIGNATIONS,
  GET_SCHOOL_REPORTING_OFFICERS,
} from '~/graphql/queries/school'
import {
  UPDATE_STAFF_CONFIG,
  ADD_STAFF_CONFIG,
  UPDATE_STAFF_ROLE,
  UPDATE_STAFF_STATUS,
} from '~/graphql/mutations/school'
import defaultUserAvatar from '~/assets/images/default-avatar.png'
import { USER_ROLES } from '~/global/data/roles'

export const mutationTypes = {
  SET_FETCHING_SCHOOL_PROFILE: 'SET_FETCHING_SCHOOL_PROFILE',
  SET_FETCH_SCHOOL_PROFILE_ERROR: 'SET_FETCH_SCHOOL_PROFILE_ERROR',
  SET_SCHOOL_PROFILE: 'SET_SCHOOL_PROFILE',
  UPDATE_SCHOOL_PROFILE: 'UPDATE_SCHOOL_PROFILE',
  SET_FETCHING_SCHOOL_STAFFS: 'SET_FETCHING_SCHOOL_STAFFS',
  SET_FETCH_SCHOOL_STAFFS_ERROR: 'SET_FETCH_SCHOOL_STAFFS_ERROR',
  SET_SCHOOL_STAFFS: 'SET_SCHOOL_STAFFS',
  UPDATE_SCHOOL_STAFFS: 'UPDATE_SCHOOL_STAFFS',
  SET_WORKING_HOURS: 'SET_WORKING_HOURS',
  SET_SCHOOL_TREE: 'SET_SCHOOL_TREE',
}

export const actionTypes = {
  FETCH_SCHOOL_PROFILE: 'FETCH_SCHOOL_PROFILE',
  FETCH_SCHOOL_STAFFS: 'FETCH_SCHOOL_STAFFS',
  FETCH_SCHOOL_STAFF_DESIGNATIONS: 'FETCH_SCHOOL_STAFF_DESIGNATIONS',
  FETCH_SCHOOL_REPORTING_OFFICERS: 'FETCH_SCHOOL_REPORTING_OFFICERS',
  WORKING_HOURS: 'WORKING_HOURS',
  UPDATE_STAFF_CONFIG: 'UPDATE_STAFF_CONFIG',
  ADD_STAFF_CONFIG: 'ADD_STAFF_CONFIG',
  FETCH_SCHOOL_TREE: 'FETCH_SCHOOL_TREE',
  UPDATE_STAFF_ROLE: 'UPDATE_STAFF_ROLE',
  UPDATE_STAFF_STATUS: 'UPDATE_STAFF_STATUS',
  GET_LIST_SCHOOL_STAFFS: 'GET_LIST_SCHOOL_STAFFS',
}

export const state = () => ({
  fetchingProfile: false,
  fetchProfileError: false,
  schoolProfile: null,
  fetchingStaffs: true,
  fetchStaffsError: false,
  schoolStaffs: [],
  workingHours: null,
  schoolTree: [],
  schoolTreeHasChildren: [],
})

export const mutations = {
  [mutationTypes.SET_FETCHING_SCHOOL_PROFILE](state, fetchingProfile) {
    state.fetchingProfile = fetchingProfile
  },
  [mutationTypes.SET_FETCH_SCHOOL_PROFILE_ERROR](state, fetchProfileError) {
    state.fetchProfileError = fetchProfileError
  },
  [mutationTypes.SET_SCHOOL_PROFILE](state, schoolProfile) {
    state.schoolProfile = schoolProfile
  },
  [mutationTypes.UPDATE_SCHOOL_PROFILE](state, schoolProfile) {
    state.schoolProfile = Object.assign({}, state.schoolProfile, schoolProfile)
  },
  [mutationTypes.SET_FETCHING_SCHOOL_STAFFS](state, fetchingStaffs) {
    state.fetchingStaffs = fetchingStaffs
  },
  [mutationTypes.SET_FETCH_SCHOOL_STAFFS_ERROR](state, fetchStaffsError) {
    state.fetchStaffsError = fetchStaffsError
  },
  [mutationTypes.SET_SCHOOL_STAFFS](state, schoolStaffs) {
    const staffs = []
    const staffsNotAdded = []
    schoolStaffs.forEach((staff) => {
      if (staff.user) {
        const staffRoles = get(staff, 'user.userRoles') || []
        staff.user.moduleRoles = groupBy(staffRoles, 'module')
      }
      ;(staff.id ? staffs : staffsNotAdded).push(staff)
    })
    state.schoolStaffs = staffs
    state.schoolStaffsNotAdded = uniqBy(staffsNotAdded, 'email')
  },
  [mutationTypes.SET_WORKING_HOURS](state, workingHours) {
    state.workingHours = workingHours
  },
  [mutationTypes.SET_SCHOOL_TREE](state, schoolTree) {
    // flatten list schools
    const flattenListSchools = (school, flattenedSchools = []) => {
      flattenedSchools.push(school)
      if (school.children && school.children.length > 0) {
        school.children.forEach((schoolItem) => {
          flattenListSchools(schoolItem, flattenedSchools)
        })
      }
      return uniqBy(flattenedSchools, 'id')
    }
    schoolTree.forEach((school) => {
      flattenListSchools(school, state.schoolTree)
    })

    state.schoolTreeHasChildren = cloneDeep(schoolTree)
  },
}

export const actions = {
  async [actionTypes.UPDATE_STAFF_ROLE]({ commit, state }, params) {
    const client = this.app.apolloProvider.defaultClient
    try {
      const { data } = await client.mutate({
        mutation: UPDATE_STAFF_ROLE,
        variables: params,
      })
      if (data) {
        return { success: true }
      }
    } catch (error) {
      console.error('UPDATE_STAFF_ROLE', error)
      return { success: false }
    }
  },
  async [actionTypes.FETCH_SCHOOL_PROFILE]({ commit, getters }, { id, children, type }) {
    try {
      commit(mutationTypes.SET_FETCHING_SCHOOL_PROFILE, true)
      const client = this.app.apolloProvider.defaultClient
      const { data } = await client.query({
        query: GET_SCHOOL_INFO,
        fetchPolicy: 'no-cache',
        variables: { id },
      })

      if (data && data.schoolInfo) {
        const foundInfo = getters.schoolTree.find((school) => data.schoolInfo.id === school.id)
        commit(mutationTypes.SET_SCHOOL_PROFILE, {
          ...data.schoolInfo,
          children: children || foundInfo.children || [],
          type: type || foundInfo.type || null,
        })
      }
    } catch (error) {
      console.error('FETCH_SCHOOL_PROFILE', error)
    } finally {
      commit(mutationTypes.SET_FETCHING_SCHOOL_PROFILE, false)
    }
  },

  async [actionTypes.FETCH_SCHOOL_TREE]({ commit, state, dispatch }) {
    try {
      const client = this.app.apolloProvider.defaultClient
      const { data } = await client.query({
        query: GET_SCHOOL_TREE,
        fetchPolicy: 'no-cache',
      })
      const schoolTree = get(data.SchoolsTree, 'jsonData')
      if (schoolTree) {
        commit(mutationTypes.SET_SCHOOL_TREE, JSON.parse(schoolTree))
      }
    } catch (error) {
      console.error('FETCH_SCHOOL_TREE', { error })
    }
  },
  async [actionTypes.FETCH_SCHOOL_STAFFS]({ commit, state }, params) {
    const { id } = state.schoolProfile || {}
    const { schoolIds, noNeedStore, designation, search, noNeedLoading, page, limit = 99999 } = params || {}
    try {
      if (!noNeedLoading) {
        commit(mutationTypes.SET_FETCHING_SCHOOL_STAFFS, true)
      }
      const client = this.app.apolloProvider.defaultClient
      const { data } = await client.query({
        query: GET_SCHOOL_STAFFS,
        fetchPolicy: 'no-cache',
        variables: {
          schoolIds: schoolIds ? schoolIds.map((schoolId) => +schoolId) : [id],
          designation: designation || null,
          search: search || null,
          page: page || 1,
          limit,
        },
      })
      if (data.academySchoolStaffs && Array.isArray(data.academySchoolStaffs.data)) {
        const academySchoolStaffsProcessed = data.academySchoolStaffs.data.map((staff) => {
          const staffMedia =
            staff.media && staff.media.url
              ? staff.media
              : {
                  url: defaultUserAvatar,
                  fileName: '',
                }
          return Object.assign({}, staff, {
            media: staffMedia,
          })
        })
        if (!noNeedStore) {
          commit(mutationTypes.SET_SCHOOL_STAFFS, academySchoolStaffsProcessed)
        }
        commit(mutationTypes.SET_FETCHING_SCHOOL_STAFFS, false)
        return { data: academySchoolStaffsProcessed, meta: data.academySchoolStaffs.meta }
      }
      throw data
    } catch (error) {
      commit(mutationTypes.SET_FETCHING_SCHOOL_STAFFS, false)
      throw error
    }
  },
  async [actionTypes.GET_LIST_SCHOOL_STAFFS]({ commit, state }, params) {
    try {
      const client = this.app.apolloProvider.defaultClient
      const { data } = await client.query({
        query: GET_SCHOOL_STAFFS,
        fetchPolicy: 'no-cache',
        variables: params,
      })
      return { data, success: true }
    } catch (error) {
      return { error, success: false }
    }
  },
  async [actionTypes.WORKING_HOURS]({ commit, getters }) {
    try {
      const client = this.app.apolloProvider.defaultClient
      const { data } = await client.query({
        query: WORKING_HOURS,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first',
        variables: {
          schoolId: getters.schoolId,
        },
      })
      if (data.academyStaffConfig) {
        commit(mutationTypes.SET_WORKING_HOURS, data.academyStaffConfig.workingHours)
        return { success: true, data: data.academyStaffConfig }
      }
    } catch (error) {
      console.error('FETCH_WORKING_HOURS', error)
      return { success: false }
    }
  },
  async [actionTypes.ADD_STAFF_CONFIG]({ getters }) {
    try {
      const client = this.app.apolloProvider.defaultClient
      const { data } = await client.mutate({
        mutation: ADD_STAFF_CONFIG,
        fetchPolicy: 'no-cache',
        variables: {
          input: {
            schoolId: getters.schoolId,
            workingHours: JSON.stringify(getters.workingHours),
          },
        },
      })
      if (data.academyStaffConfigAdd) {
        return { success: true, data: data.academyStaffConfigAdd }
      }
    } catch (error) {
      console.error('ADD_STAFF_CONFIG', error)
      return { success: false }
    }
  },
  async [actionTypes.UPDATE_STAFF_CONFIG]({ getters }) {
    try {
      const client = this.app.apolloProvider.defaultClient
      const { data } = await client.mutate({
        mutation: UPDATE_STAFF_CONFIG,
        fetchPolicy: 'no-cache',
        variables: {
          input: {
            schoolId: getters.schoolId,
            workingHours: JSON.stringify(getters.workingHours),
          },
        },
      })
      if (data.academyStaffConfigUpdate) {
        return { success: true, data: data.academyStaffConfigUpdate }
      }
    } catch (error) {
      console.error('UPDATE_STAFF_CONFIG', error)
      return { success: false }
    }
  },
  async [actionTypes.UPDATE_STAFF_STATUS]({ commit }, params) {
    try {
      commit(mutationTypes.SET_FETCHING_SCHOOL_STAFFS, true)
      const client = this.app.apolloProvider.defaultClient
      const { data } = await client.mutate({
        mutation: UPDATE_STAFF_STATUS,
        variables: params,
      })
      return { success: true }
    } catch (error) {
      console.error(actionTypes.UPDATE_STAFF_STATUS, error)
      return { success: false }
    } finally {
      commit(mutationTypes.SET_FETCHING_SCHOOL_STAFFS, false)
    }
  },
  async [actionTypes.FETCH_SCHOOL_STAFF_DESIGNATIONS]({ state }, schoolIds) {
    try {
      const { id } = state.schoolProfile || {}
      const client = this.app.apolloProvider.defaultClient
      const { data } = await client.query({
        query: GET_SCHOOL_STAFF_DESIGNATIONS,
        fetchPolicy: 'no-cache',
        variables: {
          schoolIds: schoolIds && schoolIds.length > 0 ? schoolIds.map((schoolId) => +schoolId) : [id],
        },
      })

      return { success: true, data: data.academySchoolStaffDesignations }
    } catch (error) {
      console.error('FETCH_SCHOOL_STAFF_DESIGNATIONS', error)
      return { success: false }
    }
  },
  async [actionTypes.FETCH_SCHOOL_REPORTING_OFFICERS](_, schoolId) {
    try {
      if (!schoolId) return { success: false }
      const client = this.app.apolloProvider.defaultClient
      const { data } = await client.query({
        query: GET_SCHOOL_REPORTING_OFFICERS,
        fetchPolicy: 'no-cache',
        variables: {
          schoolId,
        },
      })

      return { success: true, data: data.academyReportingOfficers }
    } catch (error) {
      console.error('FETCH_SCHOOL_REPORTING_OFFICERS', error)
      return { success: false }
    }
  },
}

export const getters = {
  schoolProfile: (state) => state.schoolProfile,
  schoolOrgId: (state) => {
    const orgId = state.schoolProfile?.parentId || state.schoolProfile?.id
    return orgId ? `${orgId}` : null
  },
  schoolId: (state) => get(state.schoolProfile, 'id', null),
  fetchingProfile: (state) => state.fetchingProfile,
  schoolStaffsWithoutCurrentUser: (state, getters, rootState) =>
    state.schoolStaffs.filter((staff) => staff.id !== rootState.profile.userProfile.id),
  schoolStaffs: (state) => state.schoolStaffs,
  schoolStaffsNotAdded: (state) => state.schoolStaffsNotAdded,
  fetchingStaffs: (state) => state.fetchingStaffs,
  fetchStaffsError: (state) => state.fetchStaffsError,
  getStaffDetail: (state) => (staffId) => {
    return state.schoolStaffs.find((staff) => staff.userId === staffId)
  },
  getStaffAcademyRole: (state, getters, rootState, rootGetters) => (staffId) => {
    const found = get(getters.getStaffDetail(staffId), 'user.userRoles', [])?.find(
      (role) => role.module === 'ACADEMY' && !role.endDate && role.schoolId === rootGetters['school/schoolId']
    )
    return found ? found.name : null
  },
  isStaffRoleAcademy: (state, getters) => (staffId) => {
    return [USER_ROLES.ACADEMY_MANAGER, USER_ROLES.ACADEMY_TRAINEE].includes(getters.getStaffAcademyRole(staffId))
  },
  isStaffRoleTraining: (state, getters) => (staffId) => {
    return [USER_ROLES.TRAINING_MANAGER, USER_ROLES.TRAINEE].includes(getters.getStaffAcademyRole(staffId))
  },
  workingHours: ({ workingHours }) => workingHours,
  schoolTree: (state) => state.schoolTree,
  schoolTreeHasChildren: (state) => state.schoolTreeHasChildren,
  currentSchoolWithChildren: (state) => {
    const result = []
    const flattenListSchools = (school, flattenedSchools = []) => {
      flattenedSchools.push(school)
      if (school.children && school.children.length > 0) {
        school.children.forEach((schoolItem) => {
          flattenListSchools(schoolItem, flattenedSchools)
        })
      }
      return uniqBy(flattenedSchools, 'id')
    }
    if (state.schoolProfile.children && state.schoolProfile.children.length > 0) {
      result.push(state.schoolProfile)
      state.schoolProfile.children.forEach((school) => {
        flattenListSchools(school, result)
      })
    }
    return result
  },
  isCurrentSchoolHQ: (state) => state.schoolProfile.type === 'hq',
}
